import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpParams } from '@angular/common/http';
import { CommonService } from '../../_services/common.service';
import { API_URL, DICE_URL } from '../../_common/constants';
import { Observable, of, throwError, iif } from 'rxjs';
import { map, catchError, retryWhen, concatMap, delay } from 'rxjs/operators';
import { ModalService } from 'src/app/_services/modal.service';
import { FileDownloadService } from 'src/app/_services/file-download.service';
import { StatusCode } from 'src/app/_common/_enum/module.enum';
import { POLLING_CONFIG } from 'src/app/_common/product-config';
import { WaiverFormsModalComponent } from '../waiver-forms-modal/waiver-forms-modal.component';

@Injectable({
  providedIn: 'root'
})
export class DiceService {

  private _psiData;
  private _diceQuote;
  private _lookupReferenceData;
  private _diceQuoteDetails;
  private _diceBindQuoteDetails;

  constructor(private commonService: CommonService, private http: HttpClient, private modalService: ModalService, private fileDownloadService: FileDownloadService) { }

  set header(data){
    this.commonService.header = data;
  }

  get runningServices(){
    return this.commonService.runningServices;
  }

  //Retrieve Initial Data from database for all form fields
  fetchReferenceData(_filterDate: any = null): Observable<HttpResponse<any>> {
    if (_filterDate) {
      let params = new HttpParams();
      params = params.append('filterdate', _filterDate);

      return this.http.get<any>(DICE_URL.LookupList, {params: params, observe: 'response'})
        .pipe(map(referenceData => {
            return referenceData.body.result;
      }));
    } else {
      return this.http.get<any>(DICE_URL.LookupList, {observe: 'response'})
        .pipe(map(referenceData => {
            return referenceData.body.result;
      }));
    }    
  }

  //Retrieve User List
  fetchUserList():Observable<HttpResponse<any>> {
    return this.http.get<any>(DICE_URL.UserList, {observe: 'response'})
      .pipe(map(company => {
          return company.body.result;
    }));
  }

  //Zip Code Search
  zipSearch(zip): Observable<HttpResponse<any>>{
    return this.http.get<any>(DICE_URL.ZipSearch+ "/" + zip, {observe: 'response'})
    .pipe(map(address => {
        return address.body.result;
    }));
  }

  //Retrieve Person List
  fetchPersonList(officerDts):Observable<HttpResponse<any>> {
    return this.http.post<any>(DICE_URL.PersonList, officerDts, {observe: 'response'})
      .pipe(map(list => {
          return list.body.result;
    }));
  }

  //Verify Address
  verifyAddress(address): Observable<HttpResponse<any>>{
    return this.http.post<any>(API_URL.VerifyAddress, address, {observe: 'response'})
        .pipe(map(response => {
          console.log('Received Address');
          return response.body.result;
    }));
  }

  //Clear Account
  clearAccount(accountDts): Observable<HttpResponse<any>>{
    return this.http.post<any>(API_URL.ClearAccount, accountDts, {observe: 'response'})
        .pipe(map(response => {
          console.log('Account Cleared for Submission!!!');
          return response.body.result;
        }));
  }

  //Quote List
  fetchQuoteList(oktaUID): Observable<HttpResponse<any>>{
    return this.http.post<any>(DICE_URL.QuotesList, {oktaUID}, {observe: 'response'})
      .pipe(map(quotelist => {
          return quotelist.body.result;
    }));
  }

  //submit Final Dice Quote
  submitDiceQuoteQueue(diceQuoteForm: any): Observable<HttpResponse<any>>{
    return this.http.post<any>(DICE_URL.SubmitApplicationQueue, diceQuoteForm, {observe: 'response'})
        .pipe(map(response => {
          console.log('Dice Form Response Received');
          return response.body.result;
    }));
  }

  //submit Copy Quote
  submitDiceCopyQuoteQueue(diceQuoteForm: any){
    return this.http.post<any>(DICE_URL.CopyQuoteQueue, diceQuoteForm, {observe: 'response'})
        .pipe(map(response => {
          console.log('Dice Copy Form First call made!');
          return response.body.result;
    }));
  }

  openModal(modalType, modalName, isKeyboard, backdropClass: string = '', isBackdrop, customWindowClass, size ="", _centered=true) {
    if(modalType === 'error'){
      this.modalService.openErrorModal();
    } else {
      this.modalService.openModal(modalName, isKeyboard, backdropClass, isBackdrop, customWindowClass, size, _centered);
    }
  }

  openWaiverModal(){
    this.openModal('none', WaiverFormsModalComponent, true, 'light-blue-backdrop', true, '');
  }

  closeModal() {
    this.modalService.closeModal();
  }

  hasOpenModals() {
    return this.modalService.hasOpenModals();
  }

  /*** Download Documents ***/
  downloadDocuments(docType: string, _documentDts: any):any {
    return this.fileDownloadService.downloadDocuments(docType, _documentDts);
  }


  downloadFile(docType: string, _documentDts: any):any {
    return this.fileDownloadService.downloadFile(docType, 'application/pdf', docType + "_" +_documentDts.accountNumber+".pdf");
  }

  /*** Fetch or Get Submitted Quote Details ***/
  fetchQuoteDetails(quotekeys) {
    return this.http.post<any>(DICE_URL.ViewQuote, quotekeys, {observe: 'response'})
      .pipe(map(quote => {
          return quote.body.result;
    }));
  }

  /*** Adding, Getting or Setting Uplaoded Documents by Broker / User ***/
  addSupportingDocument(_document){
    this.commonService.addSupportingDocument(_document);
  }

  getSupportingDocuments(){
    return this.commonService.getSupportingDocuments();
  }

  setSupportingDocuments(_documents){
    this.commonService.setSupportingDocuments(_documents);
  }

  resetSupportingDocuments(){
    return this.commonService.resetSupportingDocuments();
  }

  bindIssueQuoteQueue(_quoteDetails): Observable<HttpResponse<any>>{
  return this.http.post<any>(DICE_URL.BindnIssueQueue, _quoteDetails, {observe: 'response'})
        .pipe(map(response => {
          console.log('Dice Form Response Received');
          return response.body.result;
    }));
  }

  fetchRequoteDataQueue(_quoteDetails): Observable<HttpResponse<any>>{
    return this.http.post<any>(DICE_URL.RequoteQueue, _quoteDetails, {observe: 'response'})
        .pipe(map(response => {
          console.log('Dice Form Response Received');
          return response.body.result;
    }));
  }

  //Get Data from Local Storage
  getStorageData() {
    let _psiData = this.commonService.getData();
    return _psiData;
  }
  
  getQuoteData(){
    let _psiData = this.commonService.getData();
    return _psiData.diceQuote;
  }

  //Set Quote Data from Local Storage
  storeQuoteData(_diceQuote){
    let _psiData = this.commonService.getData();
    _psiData.diceQuote = _diceQuote;
    this.commonService.storeData(_psiData);
  }

  get diceQuote(){
    if(!this._diceQuote){
      this._diceQuote = this.getQuoteData();
    }
    console.log("Dice Service - Dice Quote :: " + this._diceQuote);
    return this._diceQuote;
  }

  set diceQuote(_diceQuote){
    this._diceQuote = _diceQuote;
    this.storeQuoteData(_diceQuote);
  }

  getReferenceData() {
    let _psiData = this.commonService.getData();
    return _psiData.lookupReferenceData;
  }

  storeReferenceData(_referenceData) {
    let _psiData = this.commonService.getData();
    _psiData.lookupReferenceData = _referenceData;
    this.commonService.storeData(_psiData);
  }

  get referenceData() {
    if(!this._lookupReferenceData) {
      this._lookupReferenceData = this.getReferenceData();
    }

    return this._lookupReferenceData;
  }

  set referenceData(_referenceData){
    this._lookupReferenceData = _referenceData;
    this.storeReferenceData(_referenceData);
  }

  getQuoteDetailsData() {
    let _psiData = this.commonService.getData();
    return _psiData.diceQuoteDetails;
  }

  storeQuoteDetailsData(_quoteDetailsData) {
    let _psiData = this.commonService.getData();
    _psiData.diceQuoteDetails = _quoteDetailsData;
    this.commonService.storeData(_psiData);
  }

  get diceQuoteDetailsData() {
    if(!this._diceQuoteDetails) {
      this._diceQuoteDetails = this.getQuoteDetailsData();
    }

    return this._diceQuoteDetails;
  }

  set diceQuoteDetailsData(_quoteDetailsData){
    this._diceQuoteDetails = _quoteDetailsData;
    this.storeQuoteDetailsData(_quoteDetailsData);
  }

  getBindData() {
    let _psiData = this.commonService.getData();
    return _psiData.bindData;
  }

  storeBindData(_bindData) {
    let _psiData = this.commonService.getData();
    _psiData.bindData = _bindData;
    this.commonService.storeData(_psiData);
  }

  get diceBindDetailsData() {
    if(!this._diceBindQuoteDetails) {
      this._diceBindQuoteDetails = this.getBindData();
    }

    return this._diceBindQuoteDetails;
  }

  set diceBindDetailsData(_bindData){
    this._diceBindQuoteDetails = _bindData;
    this.storeBindData(_bindData);
  }

  getQuoteandApplicationId () {
    let _psiData = this.commonService.getData();
    return _psiData.quoteKeys;
  }

  setQuoteandApplicationId(_quotekeys) {
    let _psiData = this.commonService.getData();
    _psiData.quoteKeys = _quotekeys;
    this.commonService.storeData(_psiData);
  }

  public removeQuoteStorageData(){
    this._psiData = this.commonService.getData();
    if(this._psiData.bindData){
      this._psiData.bindData = {};
    }
    if(this._psiData.applicationId) {
      this._psiData.applicationId = null;
    }
    if(this._psiData.quoteId) {
      this._psiData.quoteId = null;
    }
    if(this._diceBindQuoteDetails){
      this._diceBindQuoteDetails = null;
    }
    this.commonService.storeData(this._psiData);
    this.storeBindData(this._diceBindQuoteDetails);
  }
  
  getLoggedInProfile(){
    return this.commonService.getData().currentUser;
  }
  
  /** For Polling */
  queueTask(taskId){
    return this.commonService.queueTask(taskId)
  }
  /** For Polling */

  /** DandB Event Log **/
  dnbEventLog(_data) {
    this.commonService.setdandbevent(_data).subscribe((data:any) => {
      console.log('Dice service :: event fired', data);
    });
    this.commonService.storeDnBEventData(_data);
  }
  
  getDnBEventData(){
    return this.commonService.getDnBEventData();
  }
}